/* eslint-disable max-len */
// eslint-disable-next-line import/no-extraneous-dependencies
import html2pdf from 'html2pdf.js';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DatePipe } from '@angular/common';
import Qrious from 'qrious';
import { SubscriptionProductTypes } from '@rewaa-team/types';
import { SecondaryIDEnum } from '@rewaa-team/rewaa-common';
import {
  BillingAddress,
  BillingDocumentLineItem,
  PlanClassification,
  PlanItemDTO,
} from '../../../accounts/types';
import {
  MONSHAAT_PLAN_IDS,
  MONSHAAT_PLAN_IDS_FOR_PRINT,
} from '../../../accounts/constants';
import {
  LinkedPaymentWithPaymentMethod,
  MappedBillingDocumentWithPaymentReceiptData,
} from '../../types';
import { InvoiceZatcaStatusConstantOnFE } from '../../../shared/types';
import { InvoicesService } from '../../invoices.service';
import {
  REWAA_OLD_ADDRESS,
  REWAA_NEW_ADDRESS,
  REWAA_OFFICE_LOCATION_CUTOFF_DATE,
  REWAA_OLD_LOGO_SVG,
  REWAA_NEW_LOGO_SVG,
} from './rewaa-address.constants';

enum CurrencyType {
  Text = 'text',
  Symbol = 'symbol',
}

@Component({
  selector: 'rwa-invoice-print',
  templateUrl: './invoice-print.component.html',
  styleUrl: './invoice-print.component.scss',
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
})
export class InvoicePrintComponent implements OnInit {
  @Input() invoice!: MappedBillingDocumentWithPaymentReceiptData;

  @Input() isCreditNote: boolean;

  @Input() isBulkOperation = false;

  @Output() dataLoaded = new EventEmitter();

  @Input() dynamicMonshaatPlansFF = false;

  zatcaStatusConstant = InvoiceZatcaStatusConstantOnFE;

  zatcaCompleted = false;

  monshaatOrderNumber?: string;

  monshaatPlans: PlanItemDTO[] = [];

  info = {
    pages: 0,
    pageHeight: 0,
    freeSpace: 0,
    taxInvoicePages: 0,
  };

  footerMargin = 80;

  qrCode: string | undefined;

  _idsFromParams: string | undefined; // using isBulkOperation  instead now

  private readonly newTCStartFrom = '2026-06-30';

  get rewaaInfo(): string {
    const invoiceDateMs = (this.invoice?.date ?? 0) * 1000;
    const invoiceDate = new Date(invoiceDateMs);
    return invoiceDate >= REWAA_OFFICE_LOCATION_CUTOFF_DATE
      ? REWAA_NEW_ADDRESS
      : REWAA_OLD_ADDRESS;
  }

  get isNewTC(): boolean {
    const invoiceDate = new Date((this.invoice?.date ?? 0) * 1000);
    const invoiceDay = invoiceDate.toISOString().split('T')[0];
    return invoiceDay >= this.newTCStartFrom;
  }

  get isNewLogo(): boolean {
    const invoiceDateMs = (this.invoice?.date ?? 0) * 1000;
    return new Date(invoiceDateMs) >= REWAA_OFFICE_LOCATION_CUTOFF_DATE;
  }

  get rewaaEmblemLogoSrc(): string {
    const invoiceDateMs = (this.invoice?.date ?? 0) * 1000;
    const invoiceDate = new Date(invoiceDateMs);
    return invoiceDate >= REWAA_OFFICE_LOCATION_CUTOFF_DATE
      ? '/assets/images/rewaa-logo-ar-green.svg'
      : '/assets/images/rewaa-emblem-logo.svg';
  }

  watermakrDivTemplate = ` <div class="watermark">
      <div>DRAFT INVOICE</div>
      <div>فاتـــــــــــــــــــــورة مسودّة</div>
    </div>`;

  watermarkDiv = ` <div class="watermark">
      <div>DRAFT INVOICE</div>
      <div>فاتـــــــــــــــــــــورة مسودّة</div>
    </div>`;

  watermarkDivCreditPage = ` <div class="watermark credit-note-watermark">
      <div>DRAFT CREDIT NOTE</div>
      <div>مسودة إرجاع</div>
    </div>`;

  get rewaaLogoSvg(): string {
    const invoiceDateMs = (this.invoice?.date ?? 0) * 1000;
    const invoiceDate = new Date(invoiceDateMs);
    return invoiceDate >= REWAA_OFFICE_LOCATION_CUTOFF_DATE
      ? REWAA_NEW_LOGO_SVG
      : REWAA_OLD_LOGO_SVG;
  }

  termsAndCondition = {
    url: 'https://www.rewaatech.com/%d8%a7%d9%84%d8%b4%d8%b1%d9%88%d8%b7-%d9%88-%d8%a7%d9%84%d8%a3%d8%ad%d9%83%d8%a7%d9%85/',
    qrCode: '',
  };

  saudiTimezone = 'UTC+3';

  arabicIdentifierTypes = {
    [SecondaryIDEnum.CommercialRegistration]: 'السجل التجاري',
    [SecondaryIDEnum.MonshaatCommercialRegistration]: 'السجل التجاري لمنشآت',
    [SecondaryIDEnum.MOMRALicense]: 'ترخيص وزارة الشؤون البلدية والقروية',
    [SecondaryIDEnum.MOMRALicenses]: 'ترخيص وزارة الشؤون البلدية والقروية',
    [SecondaryIDEnum.MLSDLicense]:
      'ترخيص وزارة الموارد البشرية والتنمية االجتماعية',
    [SecondaryIDEnum.MLSDLicenses]:
      'ترخيص وزارة الموارد البشرية والتنمية االجتماعية',
    [SecondaryIDEnum.SAGIALicense]: 'ترخيص الهيئة العامة للالستثمار',
    [SecondaryIDEnum.SAGIALicenses]: 'ترخيص الهيئة العامة للالستثمار',
    [SecondaryIDEnum.FreelanceLicense]: 'شهادة العمل الحر',
    [SecondaryIDEnum.OtherID]: 'معرف آخر',
  };

  constructor(
    private datePipe: DatePipe,
    private invoicesService: InvoicesService,
  ) {}

  html2pdfOptions = {
    margin: 0.15,
    filename: `${this.getInvoiceId}.pdf`,
    image: { type: 'jpeg', quality: 0.5 },
    html2canvas: {
      scale: 2,
      scrollX: 0,
      scrollY: -window.scrollY,
      useCORS: true,
    },
    jsPDF: {
      unit: 'cm',
      format: 'a4',
      orientation: 'portrait',
      precision: 32,
    },
    pagebreak: {
      mode: ['css', 'legacy'],
      before: '.html2pdf__page-break',
      after: '.page-break',
      avoid: ['tr', 'td'],
    },
  };

  preZatcaInvoice = false;

  invoiceNotes: string = '';

  buildInvoice(): void {
    switch (this.invoice.zatcaStatus) {
      case InvoiceZatcaStatusConstantOnFE.Accepted:
      case InvoiceZatcaStatusConstantOnFE.AcceptedWithWarnings:
        this.zatcaCompleted = true;
        this.qrCode = this.generateZatcaQrCode();
        break;
      case InvoiceZatcaStatusConstantOnFE.PreZatca:
        this.preZatcaInvoice = true;
        this.qrCode = this.generatePreZatcaQrCode(
          this.invoice.date,
          this.invoice.total,
          this.invoice.tax ?? 0,
          this.isCreditNote,
        );
        break;
      default:
        this.zatcaCompleted = false;
        this.preZatcaInvoice = false;
        break;
    }

    this.info.pageHeight = 1080;

    if (this.zatcaCompleted || this.preZatcaInvoice) {
      this.watermarkDiv = '';
      this.watermarkDivCreditPage = '';
    } else {
      this.watermarkDiv = this.watermakrDivTemplate;
    }

    // handling the old invoices which isn't stored in dynamodb
    if (
      this.invoice.additionalIdType ===
        SecondaryIDEnum.MonshaatCommercialRegistration &&
      !this.invoice?.monshaatCompanyName
    ) {
      this.invoice.billingAddress.company =
        this.invoice.crmInfo?.monshaatCompanyName;
    }

    if (this.isCreditNote) {
      const bankTransferDiv = document.getElementById('bank-transfer');
      bankTransferDiv?.classList.add('hide');

      const termsAndConditionDiv = document.getElementById('T&C');
      termsAndConditionDiv?.classList.add('hide');

      this.info.pages = 0;
      // Credit Note Page
      this.addCreditPage();
      this.addCreditNoteCustomerInfo();
      this.addCreditTotalDue();
      this.addAllLineItems('credit');
      this.updateTotalPageNumbers('credit');
    } else {
      this.generateTermsAndConditionQrCode();
      // Tax Invoice Page
      this.addTaxInvoicePage();
      this.addCustomerInfo();
      this.addTotalDue();
      this.addAllLineItems('tax');
      this.updateTotalPageNumbers('tax');

      // Payment Summary Page
      this.info.taxInvoicePages = this.info.pages;
      this.info.pages = 0;
      this.addPaymentSummaryPage();
      this.addPaymentSummaryCustomerInfo();
      this.addPaymentSummaryTable();
      this.addPaymentStatus();
      this.updateTotalPageNumbers('payment-summary');
    }

    // get last print-wrapper and remove extra page break
    const pages = document.getElementsByClassName('print-wrapper');
    pages[pages.length - 1].classList.remove('page-break');
    pages[pages.length - 1].classList.remove('html2pdf__page-break');

    // set all page print wrapper css height to page height
    for (let i = 0; i < pages.length; i++)
      (pages[i] as HTMLElement).style.height = `${this.info.pageHeight}px`;

    const currTitle = document.title;
    document.title = `${this.getInvoiceId} - ${
      this.invoice.billingAddress.first_name ?? ''
    }`;
    this.printInvoice();
    setTimeout(() => {
      document.title = currTitle;
    }, 1000);
  }

  async ngOnInit(): Promise<void> {
    if (!this.invoice) return;

    // Use notes from invoice object if available, otherwise set empty string
    this.invoiceNotes = this.invoice.notes || '';

    if (this.dynamicMonshaatPlansFF) {
      const allMonshaatPlans = [
        PlanClassification.MonshaatPlan,
        PlanClassification.MonshaatPlanWithNewFormat,
      ];
      try {
        this.monshaatPlans = await firstValueFrom(
          this.invoicesService.getPlansByClassification(allMonshaatPlans),
        );
      } catch {
        this.monshaatPlans = [];
      }
    }

    this.buildInvoice();
  }

  generateZatcaQrCode(): string {
    const qr = new Qrious({
      size: 480,
      value: this.invoice.zatcaQrCode,
      padding: 0,
    });

    return qr.toDataURL('image/jpeg');
  }

  getTagLengthVal = (tag: number, valueStr: string): number[] => {
    const utf8Value = decodeURIComponent(encodeURIComponent(`${valueStr}`));
    const v = new TextEncoder().encode(utf8Value);
    const tl = Uint8Array.of(tag, v.byteLength);
    return [...tl, ...v];
  };

  generatePreZatcaQrCode = (
    date: number,
    total: number,
    tax: number,
    isCreditNote: boolean,
  ): string => {
    const totalParsed = total / 100;
    const parsedDate = `${new Date(date * 1000).toISOString().split('.')[0]}Z`;

    const details = [
      ...this.getTagLengthVal(1, 'شركة رواء التقنية لتقنية المعلومات'),
      ...this.getTagLengthVal(2, '310188259700003'),
      ...this.getTagLengthVal(3, `${parsedDate}`),
      ...this.getTagLengthVal(4, `${Number(totalParsed).toFixed(2)}`),
    ];
    if (!isCreditNote) {
      details.push(
        ...this.getTagLengthVal(5, `${(Number(tax) / 100).toFixed(2)}`),
      );
    }
    const qr = new Qrious({
      size: 480,
      value: btoa(String.fromCharCode(...new Uint8Array(details))),
    });

    return qr.toDataURL('image/jpeg');
  };

  generateTermsAndConditionQrCode = (): void => {
    const qr = new Qrious({
      size: 480,
      value: this.termsAndCondition.url,
      foreground: 'rgb(114,115,127)',
    });

    this.termsAndCondition.qrCode = qr.toDataURL('image/jpeg');
  };

  isLineItemMonshaatPlan(lineItem: BillingDocumentLineItem): boolean {
    if (this.dynamicMonshaatPlansFF) {
      return this.monshaatPlans.some((plan) => plan.itemId === lineItem.itemId);
    }
    return !!(lineItem.itemId && MONSHAAT_PLAN_IDS.has(lineItem.itemId));
  }

  printInvoice(): void {
    if (this.invoice) this.dataLoaded.emit();
  }

  addTaxInvoicePage(): void {
    this.info.pages += 1;
    this.info.freeSpace = this.info.pageHeight;

    const div = document.getElementById('invoice-page');
    const printWrapper = `<div
        class="print-wrapper page-break html2pdf__page-break"
        id="p-${this.info.pages}"
      ></div>`;
    div.insertAdjacentHTML('beforeend', printWrapper);
    this.addHeader();

    // create div where all the content of the page will go in
    const printWrapperDiv = document.getElementById(`p-${this.info.pages}`);
    const contentDiv = `<div
      ${this.zatcaCompleted ? '' : 'class="body"'}
      id="content-${this.info.pages}"
    >
    ${this.watermarkDiv}
    </div>`;
    printWrapperDiv.insertAdjacentHTML('beforeend', contentDiv);

    this.addFooter();
  }

  getHeight(id: string, subtractHeight = true): number {
    const div = document.getElementById(id);
    // const rect = div.getBoundingClientRect();
    // let height = rect.height;
    // height = div.scrollHeight;
    // height = div.offsetHeight;

    const height = div.clientHeight;
    if (subtractHeight) this.info.freeSpace -= height;
    return height;
  }

  addHeader(): void {
    const isB2B = this.invoice.invoice_type === 'B2B';
    const header = `<header
      class="grid pb-3 ${this.isBulkOperation ? 'pt-6' : ''}"
      id="header-${this.info.pages}"
    ><div
      class="col-5"
    >
      <div class="fs-22 font-semibold">${
        isB2B ? 'Tax Invoice' : 'Simplified Tax Invoice'
      }</div>
      <div class="rw__fs-7 font-normal text-neutral-60">${
        isB2B ? 'فاتورة ضريبية' : 'فاتوره ضريبية مبسطه '
      }</div>
    </div>
    <div class="col-2">
      ${this.rewaaLogoSvg}
    </div>
    <div class="col-5 text-neutral-60 text-right">
      ${this.rewaaInfo}
    </div>
    </header>`;
    const div = document.getElementById(`p-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', header);
    this.getHeight(`header-${this.info.pages}`);
  }

  addFooter(): void {
    const footer = `<footer class="text-neutral-60 pt-3" id="footer-${
      this.info.pages
    }">
    <div class="grid align-items-end">
      <div class="col-9">
      ${
        (this.zatcaCompleted || this.preZatcaInvoice) && this.qrCode
          ? `
        <img
        class="qr-code"
        src="${this.qrCode}"
        alt="zatca qr code"
        />
        <div class="mt-2 ">
        This QR code is encoded as per ZATCA's invoicing requirements
        </div>
        <div>
        رمز الإستجابة السريعة مشفّر بحسب متطلبات هيئة الزكاة والضريبة والجمارك
        للفوترة الإلكترونية
        </div>
        `
          : ''
      }
      </div>
      <div class="col-3">
        <div class="text-right">Page ${
          this.info.pages
        } of <span class='total-pages'>X</span>
       - ${this.getInvoiceId}
        </div>
      </div>
    </div>
  </footer>`;
    const div = document.getElementById(`p-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', footer);
    this.getHeight(`footer-${this.info.pages}`);
  }

  addCustomerInfo(): void {
    const customerInfo = `<div
    class="grid"
    id="customer-info-${this.info.pages}"
  >
    <div class="col-3">
      <div class="font-medium">Bill to</div>
      <div class="font-medium text-neutral-60 mb-1">العميل</div>
      <div>${(this.invoice.monshaatCompanyName || this.invoice.billingAddress.company) ?? ''}</div>
      <div>
        ${this.getAddress(this.invoice.billingAddress)}
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Buyer Tax Number</div>
          <div class="font-medium text-neutral-60 mb-1">
            رقم السجل الضريبي للعميل
          </div>
          <div>${this.invoice.vatNumber ?? '--'}</div>
        </div>
        <div>
          <div class="font-medium">Buyer ID: ${
            this.invoice.additionalIdType || SecondaryIDEnum.OtherID
          }</div>
          <div class="font-medium text-neutral-60 mb-1">
            معرّف العميل: ${
              this.invoice.additionalIdValue
                ? this.arabicIdentifierTypes[this.invoice.additionalIdType]
                : this.arabicIdentifierTypes[SecondaryIDEnum.OtherID]
            }
          </div>
          <div>${this.invoice.additionalIdValue || '--'}</div>
        </div>
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Invoice Number</div>
          <div class="font-medium text-neutral-60 mb-1">رقم الفاتورة</div>
          <div>${this.getInvoiceId}</div>
        </div>
        <div>
          <div class="font-medium">Invoice Issue Date</div>
          <div class="font-medium text-neutral-60 mb-1">
            تاريخ إصدار الفاتورة
          </div>
          <div>${this.datePipe.transform(
            new Date(
              this.invoice.webhookTimestamp === '0'
                ? this.invoice.createdAt
                : parseInt(this.invoice.webhookTimestamp, 10),
            ),
            'yyyy MMM d, hh:mm:ss a',
            this.saudiTimezone,
          )}</div>
        </div>
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2 text-right">
          ${
            !this.zatcaCompleted && !this.preZatcaInvoice
              ? `<div>
                    <div class="font-medium">Reference No.</div>
                    <div class="font-medium text-neutral-60 mb-1">رقم مرجعي</div>
                    <div>${this.invoice.id}</div>
                  </div>`
              : ''
          }
        <div>
          <div class="font-medium">Supply Date</div>
          <div class="font-medium text-neutral-60 mb-1">تاريخ التسليم</div>
          <div>${
            this.invoice.date
              ? this.datePipe.transform(
                  new Date(this.invoice.date * 1000),
                  'yyyy MMM d',
                  this.saudiTimezone,
                )
              : '--'
          }</div>
        </div>
      </div>
    </div>
    ${
      this.invoice?.monshaatOrderNumber
        ? `    <div class="col-3">
      <div class="font-medium">Monshaat Order No</div>
      <div class="font-medium text-neutral-60 mb-1">رقم طلب منشآت </div>
      <div>${this.invoice.monshaatOrderNumber}</div>
    </div>`
        : ''
    }

  </div>`;

    const div = document.getElementById(`content-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', customerInfo);
    this.getHeight(`customer-info-${this.info.pages}`);
  }

  addTotalDue(): void {
    const content = `  <div class="text-right pt-2 border-top-1 border-neutral-20" id='total-due-${
      this.info.pages
    }'>
    <div class="font-medium">Total Due</div>
    <div class="font-medium text-neutral-60">إجمالي المستحق</div>
    <div class="flex gap-1 align-items-center justify-content-end">
      <div >
        <div class="rw__fs-9">${
          this.invoice.currency === CurrencyType.Text
            ? `<div class="line-height-1">
        <div class="rw__fs-9">SAR</div>
        <div class="rw__fs-11 text-neutral-60">ر.س</div>
      </div>`
            : this.getCurrencySymbol('totalDue')
        }</div>
      </div>
      <div class="rw__fs-5">${(this.invoice.total / 100).toFixed(2)}</div>
    </div>
  </div>`;
    const div = document.getElementById(`content-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', content);
    this.getHeight(`total-due-${this.info.pages}`);
  }

  addAllLineItems(type: 'tax' | 'credit'): void {
    this.addLineItemTableHeader(type);
    this.invoice.lineItems.forEach((lineItem, index) => {
      this.addSingleLineItem(lineItem, index, type);
    });
    this.addSummary(type);
  }

  addLineItemTableHeader(type: 'tax' | 'credit'): void {
    const tableHeader = `<div class="pb-2 pt-3">
    <table class="print-table print-border-table print-item-table" id="table-header-${type}-${this.info.pages}">
      <thead>
        <tr>
          <th class="text-left">
            <div class="font-medium text-neutral-60">#</div>
          </th>
          <th>
            <div class="font-medium text-left">Item / Description</div>
            <div class="font-medium text-neutral-60 text-left">الوصف / المنتج</div>
          </th>
          <th>
            <div class="font-medium">Price</div>
            <div class="font-medium text-neutral-60">سعر الوحدة</div>
          </th>
          <th>
            <div class="font-medium">Quantity</div>
            <div class="font-medium text-neutral-60">الكمية</div>
          </th>
          <th>
            <div class="font-medium">Discount</div>
            <div class="font-medium text-neutral-60">خصم</div>
          </th>
          <th>
            <div class="font-medium">Taxable Amount</div>
            <div class="font-medium text-neutral-60">المبلغ الخاضع للضريبة</div>
          </th>
          <th>
            <div class="font-medium">VAT</div>
            <div class="font-medium text-neutral-60">القيمة المضافة</div>
          </th>
          <th>
            <div class="font-medium">Amount</div>
            <div class="font-medium text-neutral-60">المجموع</div>
          </th>
        </tr>
      </thead>
      <tbody id="t-body-${type}-${this.info.pages}">
      </tbody>
    </table>
  </div>`;
    let div: HTMLElement | null;
    if (type === 'tax')
      div = document.getElementById(`content-${this.info.pages}`);
    else div = document.getElementById(`content-credit-${this.info.pages}`);

    div.insertAdjacentHTML('beforeend', tableHeader);
    this.getHeight(`table-header-${type}-${this.info.pages}`);
  }

  addSingleLineItem(
    lineItem: BillingDocumentLineItem,
    index: number,
    type: 'tax' | 'credit',
  ): void {
    const showTimePeriod = !(lineItem.type === SubscriptionProductTypes.Charge);
    const hasDiscount = (lineItem.discountAmount ?? 0) > 0;
    const isMonshaatPlan = this.isLineItemMonshaatPlan(lineItem);
    const hasTax = (lineItem.taxAmount ?? 0) > 0 && !isMonshaatPlan;
    let totalAmount = 0;
    if (isMonshaatPlan) {
      totalAmount =
        (lineItem.amount ?? 0) * (1 + (lineItem.taxRate ?? 0) / 100);
    } else {
      totalAmount =
        (lineItem.amount ?? 0) +
        (lineItem.taxAmount ?? 0) -
        (lineItem.discountAmount ?? 0);
    }

    // For discount column: show "-amount" and percentage if there's a discount, otherwise show "-"
    const discountDisplay = hasDiscount
      ? `<div>-${this.convertToPrice(lineItem.discountAmount)}</div>
         <div class="text-neutral-60">${(
           (lineItem.discountAmount /
             (lineItem.unitAmount * lineItem.quantity)) *
           100
         ).toFixed(1)}%</div>`
      : `<div>-</div>`;

    // For VAT column: show amount and percentage if there's tax, otherwise just show the tax rate
    const vatDisplay = hasTax
      ? `<div>${this.convertToPrice(lineItem.taxAmount)}</div>
         <div class="text-neutral-60">${lineItem.taxRate ?? '0.0'}%</div>`
      : `<div class="text-neutral-60">${lineItem.taxRate ?? '15'}%</div>`;

    const row = `<tr id='table-row-${type}-${index}'>
          <td class="text-left">
            <div class="font-medium text-neutral-60">${index + 1}</div>
          </td>
          <td class="text-neutral-60 text-left">
            <div class="font-semibold">
              <div>${lineItem.name}</div>
              <div>${lineItem.internalName ? lineItem.internalName : ''}
              </div>
            </div>
            ${
              showTimePeriod
                ? `<div>${this.getFormattedItemStartEndTime(
                    lineItem.dateFrom,
                    lineItem.dateTo,
                  )}</div>`
                : ''
            }
          </td>
          <td>
            <div>${this.convertToPrice(lineItem.unitAmount)}</div>
          </td>
          <td>
            <div>x${lineItem.quantity}</div>
          </td>
          <td>
            ${discountDisplay}
          </td>
          <td>
            <div>${this.convertToPrice(
              (lineItem.taxableAmount ?? 0) - (lineItem.discountAmount ?? 0),
            )}</div>
          </td>
          <td>
            ${vatDisplay}
          </td>
          <td>
            <div>${this.convertToPrice(totalAmount)}</div>
          </td>
        </tr>`;

    const div = document.getElementById(`t-body-${type}-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', row);
    const rowHeight = this.getHeight(`table-row-${type}-${index}`, false);

    if (rowHeight > this.info.freeSpace - this.footerMargin) {
      div.removeChild(document.getElementById(`table-row-${type}-${index}`));
      if (type === 'tax') this.addTaxInvoicePage();
      else if (type === 'credit') this.addCreditPage();
      this.addLineItemTableHeader(type);
      const newDiv = document.getElementById(
        `t-body-${type}-${this.info.pages}`,
      );
      newDiv.insertAdjacentHTML('beforeend', row);
      this.getHeight(`table-row-${type}-${index}`);
    } else {
      this.info.freeSpace -= rowHeight;
    }
  }

  isMonshaatWithNewFormatInvoice(): boolean {
    if (this.dynamicMonshaatPlansFF) {
      const monshaatPlansWithNewFormat = this.monshaatPlans.filter(
        (plan) =>
          plan.planClassification ===
          PlanClassification.MonshaatPlanWithNewFormat,
      );
      return this.invoice?.lineItems?.some(
        (item) =>
          item.itemId &&
          monshaatPlansWithNewFormat.some(
            (plan) => plan.itemId === item.itemId,
          ),
      );
    }
    return this.invoice?.lineItems?.some(
      (item) => item.itemId && MONSHAAT_PLAN_IDS_FOR_PRINT.has(item.itemId),
    );
  }

  addSummary(type: 'tax' | 'credit'): void {
    const { totalDiscount, printingSubtotal } = this.invoice;
    const discountPercentage =
      printingSubtotal === 0
        ? 0
        : (((totalDiscount ?? 0) / printingSubtotal) * 100).toFixed(1);

    // Check if this is a Monshaat invoice (has Monshaat plan in line items)

    const summary = this.isMonshaatWithNewFormatInvoice()
      ? this.getMonshaatSummaryHtml(type)
      : this.getStandardSummaryHtml(type, discountPercentage);

    let div: HTMLElement | null;
    if (type === 'tax')
      div = document.getElementById(`content-${this.info.pages}`);
    else div = document.getElementById(`content-credit-${this.info.pages}`);

    div.insertAdjacentHTML('beforeend', summary);
    const summaryHeight = this.getHeight(`summary-${type}`, false);

    if (summaryHeight > this.info.freeSpace - this.footerMargin) {
      div.removeChild(document.getElementById(`summary-${type}`));
      if (type === 'tax') this.addTaxInvoicePage();
      else if (type === 'credit') this.addCreditPage();
      this.addSummary(type);
    } else {
      this.info.freeSpace -= summaryHeight;
    }
  }

  private getStandardSummaryHtml(
    type: 'tax' | 'credit',
    discountPercentage: string | number,
  ): string {
    return `<div class="grid" id="summary-${type}">
    <div class="col-6">
      <div class="font-medium">Notes</div>
      <div class="font-medium text-neutral-60 mb-1">ملاحظات</div>
      <div>${this.escapeHtml(this.invoiceNotes || '-')}</div>
    </div>
    <div class="col-6">
      <table class="print-table print-item-table">
        <tbody>
          <tr>
            <td>
              <div class="font-medium">Subtotal</div>
              <div class="font-medium text-neutral-60">
                الإجمالي غير شامل الضريبة
              </div>
            </td>
            <td>
              <div class="font-medium text-neutral-60">${
                this.invoice.currency === CurrencyType.Text
                  ? `<div class="rw__fs-9">SAR</div>
                    <div class="rw__fs-11 text-neutral-60">ر.س</div>`
                  : this.getCurrencySymbol()
              }</div>
            </td>
            <td>
              <div>${this.convertToPrice(this.invoice.printingSubtotal)}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total Discount</div>
              <div class="font-medium text-neutral-60">إجمالي الخصم</div>
            </td>
            <td>
              <div>
              ${
                this.invoice.currency === CurrencyType.Text
                  ? `<div class="rw__fs-9">SAR</div>
                    <div class="rw__fs-11 text-neutral-60">ر.س</div>`
                  : this.getCurrencySymbol()
              }
              </div>
            </td>
            <td>
              <div>-${
                this.convertToPrice(this.invoice.totalDiscount) ?? 0
              }</div>
              <div class="text-neutral-60">${discountPercentage}%</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total Taxable Amount</div>
              <div class="font-medium text-neutral-60">
                إجمالي المبلغ الخاضع للضريبة
              </div>
            </td>
            <td>
              <div>${
                this.invoice.currency === CurrencyType.Text
                  ? `<div class="rw__fs-9">SAR</div>
                    <div class="rw__fs-11 text-neutral-60">ر.س</div>`
                  : this.getCurrencySymbol()
              }</div>
            </td>
            <td>
              <div>${this.convertToPrice(this.invoice.totalTaxableAmount)}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total VAT</div>
              <div class="font-medium text-neutral-60">
                إجمالي ضريبة القيمة المضافة
              </div>
            </td>
            <td>
              <div>${
                this.invoice.currency === CurrencyType.Text
                  ? `<div class="rw__fs-9">SAR</div>
                    <div class="rw__fs-11 text-neutral-60">ر.س</div>`
                  : this.getCurrencySymbol()
              }</div>
            </td>
            <td>
              <div>${this.convertToPrice(this.invoice.tax)}</div>
              <div class="text-neutral-60">${this.taxPercentage}%</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total</div>
              <div class="font-medium text-neutral-60">
                الإجمالي شامل الضريبة
              </div>
            </td>
            <td>
              <div>${
                this.invoice.currency === CurrencyType.Text
                  ? `<div class="rw__fs-9">SAR</div>
                    <div class="rw__fs-11 text-neutral-60">ر.س</div>`
                  : this.getCurrencySymbol()
              }</div>
            </td>
            <td>
              <div class="font-medium">${this.convertToPrice(
                this.invoice.total,
              )}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>`;
  }

  private getMonshaatSummaryHtml(type: 'tax' | 'credit'): string {
    // Calculate Total VAT Incl before discount (printingSubtotal * 1.15 for VAT)
    const taxRate = 0.15;
    const totalVatInclBeforeDiscount =
      (this.invoice.printingSubtotal ?? 0) +
      (this.invoice.printingSubtotal ?? 0) * taxRate;

    // Calculate Total Taxable Amount and Total VAT from the final total (after discounts)
    // Total = Taxable + VAT, where VAT = Taxable * 0.15
    // Total = Taxable * 1.15, so Taxable = Total / 1.15
    const totalVatInc = this.invoice.total ?? 0;
    const totalTaxableAmount = Math.round(totalVatInc / (1 + taxRate));
    const totalVat = totalVatInc - totalTaxableAmount;
    const monshaatDiscountVatIncl =
      (this.invoice.monshaatDiscountVatIncl ?? 0) +
      (this.invoice.monshaatDiscountVatIncl ?? 0) * taxRate;
    const rewaaDiscountVatIncl =
      (this.invoice.rewaaDiscountVatIncl ?? 0) +
      (this.invoice.rewaaDiscountVatIncl ?? 0) * taxRate;

    const currencySymbol =
      this.invoice.currency === CurrencyType.Text
        ? `<div class="rw__fs-9">SAR</div>
           <div class="rw__fs-11 text-neutral-60">ر.س</div>`
        : this.getCurrencySymbol();

    return `<div class="grid" id="summary-${type}">
    <div class="col-6">
      <div class="font-medium">Notes</div>
      <div class="font-medium text-neutral-60 mb-1">ملاحظات</div>
      <div>${this.escapeHtml(this.invoiceNotes || '-')}</div>
    </div>
    <div class="col-6">
      <table class="print-table print-item-table">
        <tbody>
          <tr>
            <td>
              <div class="font-medium">Total VAT Incl before discount</div>
              <div class="font-medium text-neutral-60">
                الإجمالي شامل الضريبة قبل الخصم
              </div>
            </td>
            <td>
              <div>${currencySymbol}</div>
            </td>
            <td>
              <div>${this.convertToPrice(totalVatInclBeforeDiscount)}</div>
            </td>
          </tr>
          ${
            (monshaatDiscountVatIncl ?? 0) > 0
              ? `<tr>
            <td>
              <div class="font-medium">Monshaat Discount VAT Incl</div>
              <div class="font-medium text-neutral-60">
                قيمة دعم منشآت شامل الضريبة
              </div>
            </td>
            <td>
              <div>${currencySymbol}</div>
            </td>
            <td>
              <div>(${this.convertToPrice(monshaatDiscountVatIncl)})</div>
            </td>
          </tr>`
              : ''
          }
          ${
            (rewaaDiscountVatIncl ?? 0) > 0
              ? `<tr>
            <td>
              <div class="font-medium">Rewaa Discount VAT Incl</div>
              <div class="font-medium text-neutral-60">
                قسيمة رواء شامل الضريبة
              </div>
            </td>
            <td>
              <div>${currencySymbol}</div>
            </td>
            <td>
              <div>(${this.convertToPrice(rewaaDiscountVatIncl)})</div>
            </td>
          </tr>`
              : ''
          }
          <tr>
            <td>
              <div class="font-medium">Total VAT Inc</div>
              <div class="font-medium text-neutral-60">
                الإجمالي شامل الضريبة
              </div>
            </td>
            <td>
              <div>${currencySymbol}</div>
            </td>
            <td>
              <div class="font-medium">${this.convertToPrice(totalVatInc)}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total Taxable Amount</div>
              <div class="font-medium text-neutral-60">
                إجمالي المبلغ الخاضع للضريبة
              </div>
            </td>
            <td>
              <div>${currencySymbol}</div>
            </td>
            <td>
              <div>${this.convertToPrice(totalTaxableAmount)}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total VAT</div>
              <div class="font-medium text-neutral-60">
                إجمالي ضريبة القيمة المضافة
              </div>
            </td>
            <td>
              <div>${currencySymbol}</div>
            </td>
            <td>
              <div>${this.convertToPrice(totalVat)}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>`;
  }

  updateTotalPageNumbers(type: 'tax' | 'credit' | 'payment-summary'): void {
    let totalPageSpans: HTMLCollectionOf<Element>;
    if (type === 'tax')
      totalPageSpans = document.getElementsByClassName('total-pages');
    else if (type === 'credit')
      totalPageSpans = document.getElementsByClassName('total-credit-pages');
    else if (type === 'payment-summary')
      totalPageSpans = document.getElementsByClassName(
        'total-payment-summary-pages',
      );

    for (let i = 0; i < totalPageSpans.length; i++) {
      let pageCount: string;
      if (type === 'tax') {
        const taxTermsExtraPages = this.isNewTC ? 4 : 2;
        pageCount = (this.info.pages + taxTermsExtraPages).toString();
      } else {
        pageCount = this.info.pages.toString();
      }
      totalPageSpans[i].innerHTML = pageCount;
    }
  }

  // ------- Credit Notes Page -----
  addCreditPage(): void {
    this.info.pages += 1;
    this.info.freeSpace = this.info.pageHeight;

    const div = document.getElementById('credit-page');
    const printWrapper = `<div
        class="print-wrapper page-break html2pdf__page-break"
        id="p-credit-${this.info.pages}"
      ></div>`;
    div.insertAdjacentHTML('beforeend', printWrapper);
    this.addCreditNoteHeader();

    // create div where all the content of the page will go in
    const printWrapperDiv = document.getElementById(
      `p-credit-${this.info.pages}`,
    );
    const contentDiv = `<div
    ${this.zatcaCompleted ? '' : 'class="body"'}
      id="content-credit-${this.info.pages}"
    > ${this.watermarkDivCreditPage}</div>`;
    printWrapperDiv.insertAdjacentHTML('beforeend', contentDiv);

    this.addCreditFooter();
  }

  addCreditNoteHeader(): void {
    const header = `<header
      class="grid pb-3"
      id="header-credit-${this.info.pages}"
    ><div
      class="col-5"
    >
      <div class="fs-22 font-semibold">Credit Note</div>
      <div class="rw__fs-7 font-normal text-neutral-60">إشعار دائن</div>
    </div>
    <div class="col-2">
      ${this.rewaaLogoSvg}
    </div>
    <div class="col-5 text-neutral-60 text-right">
      ${this.rewaaInfo}
    </div>
    </header>`;
    const div = document.getElementById(`p-credit-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', header);
    this.getHeight(`header-credit-${this.info.pages}`);
  }

  addCreditFooter(): void {
    const footer = `<footer class="text-neutral-60 pt-3" id="footer-credit-${
      this.info.pages
    }">
    <div class="grid align-items-end">
      <div class="col-9">
            ${
              (this.zatcaCompleted || this.preZatcaInvoice) && this.qrCode
                ? `
              <img
              class="qr-code"
              src="${this.qrCode}"
              alt="zatca qr code"
              />
              <div class="mt-2 ">
              This QR code is encoded as per ZATCA's invoicing requirements
              </div>
              <div>
              رمز الإستجابة السريعة مشفّر بحسب متطلبات هيئة الزكاة والضريبة والجمارك
              للفوترة الإلكترونية
              </div>
              `
                : ''
            }
      </div>
      <div class="col-3">
        <div class="text-right">Page ${
          this.info.pages
        } of <span class='total-credit-pages'>X</span> - ${this.getInvoiceId}
        </div>
      </div>
    </div>
  </footer>`;
    const div = document.getElementById(`p-credit-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', footer);
    this.getHeight(`footer-credit-${this.info.pages}`);
  }

  addCreditNoteCustomerInfo(): void {
    const customerInfo = `<div
    class="grid"
    id="customer-info-credit-${this.info.pages}"
  >
    <div class="col-3">
      <div class="font-medium">Bill to</div>
      <div class="font-medium text-neutral-60 mb-1">العميل</div>
      <div>${this.invoice.billingAddress.company ?? ''}</div>
      <div>
        ${this.getAddress(this.invoice.billingAddress)}
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Buyer Tax Number</div>
          <div class="font-medium text-neutral-60 mb-1">
            رقم السجل الضريبي للعميل
          </div>
          <div>${this.invoice.vatNumber ?? '--'}</div>
        </div>
        <div>
          <div class="font-medium ">Buyer ID: ${
            this.invoice.additionalIdType || SecondaryIDEnum.OtherID
          }</div>
          <div class="font-medium text-neutral-60 mb-1">
            معرّف العميل: ${
              this.invoice.additionalIdValue
                ? this.arabicIdentifierTypes[this.invoice.additionalIdType]
                : this.arabicIdentifierTypes[SecondaryIDEnum.OtherID]
            }
          </div>
          <div>${this.invoice.additionalIdValue || '--'}</div>
        </div>
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Credit Note Number</div>
          <div class="font-medium text-neutral-60 mb-1"> رقم إشعار دائن</div>
          <div>${this.getInvoiceId}</div>
        </div>
        <div>
          <div class="font-medium">Credit Note Issue Date</div>
          <div class="font-medium text-neutral-60 mb-1">
              تاريخ إصدار إشعار دائن
          </div>
          <div>${this.datePipe.transform(
            new Date(this.invoice.date * 1000),
            'yyyy MMM d, hh:mm:ss a',
            this.saudiTimezone,
          )}</div>
        </div>
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2 text-right">
        <div>
          <div class="font-medium">Invoice Number</div>
          <div class="font-medium text-neutral-60 mb-1">
              رقم الفاتورة
          </div>
          <div>${
            this.invoice.sequencedOldReferenceId ||
            this.invoice.sequencedReferenceId ||
            '--'
          }</div>
        </div>
      </div>
    </div>
  </div>`;

    const div = document.getElementById(`content-credit-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', customerInfo);
    this.getHeight(`customer-info-credit-${this.info.pages}`);
  }

  addCreditTotalDue(): void {
    const content = `  <div class="text-right pt-2 border-top-1 border-neutral-20" id='total-credit-due-${
      this.info.pages
    }'>
    <div class="font-medium">Total Credit Note Amount</div>
    <div class="font-medium text-neutral-60">إجمالي قيمة إشعار الدائن</div>
    <div class="flex gap-1 align-items-center justify-content-end">
      <div class="line-height-1">
        <div class="rw__fs-9">
        ${
          this.invoice.currency === CurrencyType.Text
            ? `<div class="line-height-1">
        <div class="rw__fs-9">SAR</div>
        <div class="rw__fs-11 text-neutral-60">ر.س</div>
      </div>`
            : this.getCurrencySymbol('totalDue')
        }
        </div>
      </div>
      <div class="rw__fs-5">${this.convertToPrice(this.invoice.total)}</div>
    </div>
  </div>`;
    const div = document.getElementById(`content-credit-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', content);
    this.getHeight(`total-credit-due-${this.info.pages}`);
  }

  // ------ Payment Summary Page -------

  addPaymentSummaryPage(): void {
    this.info.pages += 1;
    this.info.freeSpace = this.info.pageHeight;

    const div = document.getElementById('payment-summary-page');
    const printWrapper = `<div

        class="print-wrapper page-break html2pdf__page-break"
        id="p-payment-summary-${this.info.pages}"
      ></div>`;
    div.insertAdjacentHTML('beforeend', printWrapper);
    this.addPaymentSummaryHeader();

    // create div where all the content of the page will go in
    const printWrapperDiv = document.getElementById(
      `p-payment-summary-${this.info.pages}`,
    );
    const contentDiv = `<div
      ${this.zatcaCompleted ? '' : 'class="body"'}
      id="content-payment-summary-${this.info.pages}"
    >
    ${this.watermarkDiv}
      </div>`;
    printWrapperDiv.insertAdjacentHTML('beforeend', contentDiv);

    this.addPaymentSummaryFooter();
  }

  addPaymentSummaryHeader(): void {
    const header = `<header
      class="grid pb-3 ${this.isBulkOperation ? 'pt-6' : ''}"
      id="header-payment-summary-${this.info.pages}"
    ><div
      class="col-5"
    >
      <div class="fs-22 font-semibold">Payment Summary</div>
      <div class="rw__fs-7 font-normal text-neutral-60">ملخّص المدفوعات</div>
    </div>
    <div class="col-2">
      ${this.rewaaLogoSvg}
    </div>
    <div class="col-5 text-neutral-60 text-right">
      ${this.rewaaInfo}
    </div>
    </header>`;
    const div = document.getElementById(`p-payment-summary-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', header);
    this.getHeight(`header-payment-summary-${this.info.pages}`);
  }

  addPaymentSummaryFooter(): void {
    const footer = `
        <footer class="text-neutral-60 pt-3" id="footer-payment-summary-${
          this.info.pages
        }">
        <div class="grid align-items-end">
          <div class="col-9 fs-footer">
            ${
              this.zatcaCompleted && this.qrCode
                ? `
              <div class="mt-2 ">
              This transaction is subject to Rewaa's terms and conditions and privacy policy, which you can view on Rewaa's official website
              </div>
              <div>
           هذه المعاملة خاضعة للشروط والأحكام وسياسة الخصوصية الخاصة بشركة رِواء، والتي يمكنك الإطلاع عليها من خلال موقع رِواء الرسمي
              </div>
              `
                : ''
            }
          </div>
          <div class="col-3">
            <div class="text-right">Page ${
              this.info.pages
            } of <span class='total-payment-summary-pages'>X</span> ${
              this.getInvoiceId
            }</div>
          </div>
        </div>
      </footer>`;
    const div = document.getElementById(`p-payment-summary-${this.info.pages}`);
    div.insertAdjacentHTML('beforeend', footer);
    this.getHeight(`footer-payment-summary-${this.info.pages}`);
  }

  addPaymentSummaryCustomerInfo(): void {
    const customerInfo = `
      <div class="grid"
      id="customer-info-payment-summary-${this.info.pages}"
      >
    <div class="col-3">
      <div class="font-medium">Customer</div>
      <div class="font-medium text-neutral-60 mb-1">العميل</div>
      <div>${this.invoice.billingAddress.first_name ?? ''}</div>
      <div>
      ${this.getAddress(this.invoice.billingAddress)}
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Buyer Tax Number</div>
          <div class="font-medium text-neutral-60 mb-1">
            رقم السجل الضريبي للعميل
          </div>
          <div>${this.invoice.vatNumber ?? '--'}</div>
        </div>
        <div>
  <div class="font-medium ">Buyer ID: ${
    this.invoice.additionalIdType || SecondaryIDEnum.OtherID
  }</div>
          <div class="font-medium text-neutral-60 mb-1">
            معرّف العميل: ${
              this.invoice.additionalIdValue
                ? this.arabicIdentifierTypes[this.invoice.additionalIdType]
                : this.arabicIdentifierTypes[SecondaryIDEnum.OtherID]
            }
          </div>
          <div>${this.invoice.additionalIdValue || '--'}</div>
        </div>
      </div>
    </div>
    <div class="col-3">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Invoice Number</div>
          <div class="font-medium text-neutral-60 mb-1">رقم الفاتورة</div>
          <div>${this.getInvoiceId}</div>
        </div>
      </div>
    </div>
    <div class="col-3 text-right">
      <div class="flex flex-column gap-2">
        <div>
          <div class="font-medium">Last Updated</div>
          <div class="font-medium text-neutral-60 mb-1">تاريخ آخر تحديث</div>
          <div>${this.datePipe.transform(
            new Date(this.invoice.lastUpdated * 1000),
            'yyyy MMM d, hh:mm:ss a',
            this.saudiTimezone,
          )}</div>
        </div>
      </div>
    </div>
  </div>`;

    const div = document.getElementById(
      `content-payment-summary-${this.info.pages}`,
    );
    div.insertAdjacentHTML('beforeend', customerInfo);
    this.getHeight(`customer-info-payment-summary-${this.info.pages}`);
  }

  addPaymentSummaryTableHeader(): void {
    const tableHeader = `  <div class="pb-2 pt-6">
    <table class="print-table print-border-table payment-table">
      <thead>
        <tr>
          <th class="text-left">
            <div class="font-medium">Transaction Date</div>
            <div class="font-medium text-neutral-60">تاريخ العملية</div>
          </th>
          <th class="text-left">
            <div class="font-medium">Payment Method</div>
            <div class="font-medium text-neutral-60">طريقة الدفع</div>
          </th>
          <th class="text-left">
            <div class="font-medium">Reference No.</div>
            <div class="font-medium text-neutral-60">الرقم المرجعي</div>
          </th>
          <th>
            <div class="font-medium">Amount Paid</div>
            <div class="font-medium text-neutral-60">المبلغ المدفوع</div>
          </th>
        </tr>
      </thead>
      <tbody id="t-body-payment-summary-${this.info.pages}">
      </tbody>
    </table>
  </div>`;

    const div = document.getElementById(
      `content-payment-summary-${this.info.pages}`,
    );
    div.insertAdjacentHTML('beforeend', tableHeader);
    this.getHeight(`t-body-payment-summary-${this.info.pages}`);
  }

  addPaymentSummaryTableRow(
    txnData: LinkedPaymentWithPaymentMethod,
    index: number,
  ): void {
    const row = `<tr id="table-row-payment-summary-${index}">
          <td class="text-left">
            <div class="font-semibold text-neutral-60">${this.datePipe.transform(
              new Date(txnData.txn_date * 1000),
              'dd MMM yyyy',
              this.saudiTimezone,
            )}</div>
          </td>
          <td class="text-left">
            <div>${txnData.paymentMethod ?? ''}</div>
            <div class="text-neutral-60">${txnData.bankName ?? ''}</div>
          </td>
          <td class="text-left">
            <div>${txnData.txn_id}</div>
          </td>
          <td>
            <div class="font-semibold">${this.convertToPrice(
              txnData.txn_amount,
            )}</div>
          </td>
        </tr>`;
    const div = document.getElementById(
      `t-body-payment-summary-${this.info.pages}`,
    );
    div.insertAdjacentHTML('beforeend', row);
    const rowHeight = this.getHeight(
      `table-row-payment-summary-${index}`,
      false,
    );

    if (rowHeight > this.info.freeSpace - this.footerMargin) {
      div.removeChild(
        document.getElementById(`table-row-payment-summary-${index}`),
      );
      this.addPaymentSummaryPage();
      this.addPaymentSummaryTableHeader();
      const newDiv = document.getElementById(
        `t-body-payment-summary-${this.info.pages}`,
      );
      newDiv.insertAdjacentHTML('beforeend', row);
      this.getHeight(`table-row-payment-summary-${index}`);
    } else this.info.freeSpace -= rowHeight;
  }

  addSummaryForPaymentSummaryPage(): void {
    const summary = `
      <div class="grid" id="summary-payment-summary">
    <div class="col-6">
      <div class="font-medium">Notes</div>
      <div class="font-medium text-neutral-60 mb-1">ملاحظات</div>
      <div>${this.escapeHtml(this.invoiceNotes || '-')}</div>
    </div>
    <div class="col-6">
      <table class="print-table print-item-table">
        <tbody>
          <tr>
            <td>
              <div class="font-medium">Invoice Total</div>
              <div class="font-medium text-neutral-60">
                إجمالي الفاتورة شامل الضريبة
              </div>
            </td>
            <td>
              <div id="complete-appear">
                      ${
                        this.invoice.currency === CurrencyType.Text
                          ? `<div id="complete-appear">SAR</div>
              <div class="font-medium text-neutral-60">ر.س</div>`
                          : this.getCurrencySymbol()
                      }</div>
            </td>
            <td>
              <div>${this.convertToPrice(this.invoice.total)}</div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="font-medium">Total Collected Amount</div>
              <div class="font-medium text-neutral-60">
                إجمالي المبالغ المحصّلة
              </div>
            </td>
            <td>
              <div>
              ${
                this.invoice.currency === CurrencyType.Text
                  ? `<div>SAR</div>
              <div class="font-medium text-neutral-60">ر.س</div>`
                  : this.getCurrencySymbol()
              }</div>
            </td>
            <td>
              <div>-${this.convertToPrice(
                this.invoice.totalCollectedAmount,
              )}</div>
            </td>
          </tr>
          <tr class="fs-7 text-neutral-60">
            <td class="pt-0">
              <div>Total Amount Paid</div>
              <div class="font-medium">إجمالي المبالغ المدفوعة</div>
            </td>
            <td class="pt-0">
              <div>
              ${
                this.invoice.currency === CurrencyType.Text
                  ? `<div>SAR</div>
              <div>ر.س</div>`
                  : this.getCurrencySymbol()
              }
              </div>
            </td>
            <td class="pt-0">
              <div>${this.convertToPrice(this.invoice.amountPaid)}</div>
            </td>
          </tr>
          <tr class="fs-7 text-neutral-60">
            <td class="pt-0">
              <div>Customer Credit</div>
              <div>رصيد العميل</div>
            </td>
            <td class="pt-0">
              <div>
              ${
                this.invoice.currency === CurrencyType.Text
                  ? `<div>SAR</div>
              <div>ر.س</div>`
                  : this.getCurrencySymbol()
              }</div>
            </td>
            <td class="pt-0">
              <div>${this.convertToPrice(this.invoice.creditsApplied)}</div>
            </td>
          </tr>
          <tr class="border-top-1 border-neutral-20">
            <td>
              <div class="font-medium">Total Due</div>
              <div class="font-medium text-neutral-60">إجمالي المستحق</div>
            </td>
            <td>
              <div>
                ${
                  this.invoice.currency === CurrencyType.Text
                    ? `<div>SAR</div>
              <div class="font-medium text-neutral-60">ر.س</div>`
                    : this.getCurrencySymbol('totalDue')
                }
              </div>
            </td>
            <td>
              <div class="font-semibold">${this.convertToPrice(
                this.invoice.dueAmount,
              )}</div>
            </td>
          </tr>
          ${
            this.invoice.excessBalance > 0
              ? `          <tr>
            <td>
              <div class="font-medium">Excess Balance</div>
              <div class="font-medium text-neutral-60">رصيد زائد</div>
            </td>
            <td>
              <div>
                ${
                  this.invoice.currency === CurrencyType.Text
                    ? `<div>SAR</div>
              <div class="font-medium text-neutral-60">ر.س</div>`
                    : this.getCurrencySymbol()
                }
              </div>
            </td>
            <td>
              <div class="font-medium">${this.convertToPrice(
                this.invoice.excessBalance,
              )}</div>
            </td>
          </tr>`
              : ''
          }
        </tbody>
      </table>
    </div>
  </div>`;

    const div = document.getElementById(
      `content-payment-summary-${this.info.pages}`,
    );

    div.insertAdjacentHTML('beforeend', summary);
    const summaryHeight = this.getHeight(`summary-payment-summary`, false);

    if (summaryHeight > this.info.freeSpace - this.footerMargin) {
      div.removeChild(document.getElementById(`summary-payment-summary`));
      this.addPaymentSummaryPage();
      this.addSummaryForPaymentSummaryPage();
    } else {
      this.info.freeSpace -= summaryHeight;
    }
  }

  addPaymentSummaryTable(): void {
    this.addPaymentSummaryTableHeader();
    this.invoice.txnData?.forEach((txnData, index) => {
      this.addPaymentSummaryTableRow(txnData, index);
    });
    this.addSummaryForPaymentSummaryPage();
  }

  addPaymentStatus(): void {
    const { dueAmount, total } = this.invoice;
    const status =
      dueAmount > 0
        ? `<div class="flex gap-4 pt-2 justify-content-between" id="payment-status-${
            this.info.pages
          }">
      <div>
        <div class="font-medium">Payment Status</div>
        <div class="font-medium text-neutral-60">حالة الدفع</div>
        <div class="text-red-60 rw__fs-9 py-2">${
          dueAmount === total
            ? 'Not Paid غير مدفرع'
            : 'Partially Paid مدفوع جزئياً'
        }</div>
      </div>
      <div class="text-right">
        <div class="font-medium">Total Due</div>
        <div class="font-medium text-neutral-60">إجمالي المستحق</div>
        <div
          class="flex gap-1 align-items-center justify-content-end text-red-60"
        >
          <div class="line-height-1">
            <div class="rw__fs-9">
                ${
                  this.invoice.currency === CurrencyType.Text
                    ? `<div class="line-height-1">
            <div class="rw__fs-9">SAR</div>
            <div class="rw__fs-11">ر.س</div>
          </div>`
                    : this.getCurrencySymbol('totalDue', 'text-red-60')
                }
            </div>
          </div>
          <div class="rw__fs-5">${this.convertToPrice(
            this.invoice.dueAmount,
          )}</div>
        </div>
      </div>
    </div>`
        : `
        <div class="flex gap-4 pt-2 justify-content-between" id="payment-status-${
          this.info.pages
        }">
      <div>
        <div class="font-medium">Payment Status</div>
        <div class="font-medium text-neutral-60">حالة الدفع</div>
        <div class="text-green-60 rw__fs-9 py-2">
          Paid In Full مدفوعة بالكامل
        </div>
      </div>
      <div class="text-right">
        <div class="font-medium">Total Due</div>
        <div class="font-medium text-neutral-60">إجمالي المستحق</div>
        <div class="flex gap-1 align-items-center justify-content-end">
          <div class="line-height-1">
            <div class="rw__fs-9">
                ${
                  this.invoice.currency === CurrencyType.Text
                    ? `<div class="rw__fs-9">SAR</div>
            <div class="rw__fs-11 text-neutral-60">ر.س</div>`
                    : this.getCurrencySymbol('totalDue')
                }
            </div>
          </div>
          <div class="rw__fs-5">00</div>
        </div>
      </div>
    </div>`;

    const div = document.getElementById(
      `content-payment-summary-${this.info.pages}`,
    );

    div.insertAdjacentHTML('beforeend', status);
    const paymentStatusHeight = this.getHeight(
      `payment-status-${this.info.pages}`,
      false,
    );

    if (paymentStatusHeight > this.info.freeSpace - this.footerMargin) {
      div.removeChild(
        document.getElementById(`payment-status-${this.info.pages}`),
      );
      this.addPaymentSummaryPage();
      this.addPaymentStatus();
    } else {
      this.info.freeSpace -= paymentStatusHeight;
    }
  }

  // util functions
  convertToPrice(amount: number): string {
    return (amount / 100).toFixed(2);
  }

  getFormattedItemStartEndTime(dateFrom: number, dateTo: number): string {
    const from = this.datePipe.transform(
      dateFrom * 1000,
      'dd MMM yyyy',
      this.saudiTimezone,
    );
    const to = this.datePipe.transform(
      dateTo * 1000,
      'dd MMM yyyy',
      this.saudiTimezone,
    );
    return `${from}-${to}`;
  }

  getAddress(billingAddress: BillingAddress): string {
    const parts: string[] = [];
    if (billingAddress.zip) parts.push(billingAddress.zip);
    if (billingAddress.line1) parts.push(billingAddress.line1);
    if (billingAddress.line2) parts.push(billingAddress.line2);
    if (billingAddress.line3) parts.push(billingAddress.line3);
    if (billingAddress.city) parts.push(billingAddress.city);
    if (billingAddress.country) parts.push(billingAddress.country);
    return parts.join(', ');
  }

  get getInvoiceId(): string {
    if (!this.invoice) return '';

    return (
      this.invoice.oldSequencedId || this.invoice.sequencedId || this.invoice.id
    );
  }

  get taxPercentage(): string {
    const { lineItems } = this.invoice;
    if (!lineItems) return '0.0';

    if (lineItems.some((item) => item.taxRate === 15)) return '15';
    return '0.0';
  }

  /**
   * @deprecated
   *use bulkInvoicePrint component to handle printing
   *will be removed after using the new component in prod
   */
  async fetchDataAndBuildInvoice(id: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.invoicesService.getInvoiceById(id),
      );
      this.invoice = response;
      this.buildInvoice();

      const element = document.getElementById('entire-thing');
      if (element) {
        this.html2pdfOptions.filename = `${this.getInvoiceId}.pdf`;
        html2pdf(element, this.html2pdfOptions);

        await new Promise((resolve) => {
          setTimeout(() => {
            document.getElementById('invoice-page').innerHTML = '';
            document.getElementById('payment-summary-page').innerHTML = '';
            resolve(null);
          }, 500);
        });
      }
    } catch (err) {
      console.error('Error fetching invoice', err);
    }
  }

  /**
   * @deprecated
   *use bulkInvoicePrint component to handle printing
   *will be removed after using the new component in prod
   */
  async fetchDataAndBuildCreditNote(id: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.invoicesService.getCreditNoteById(id),
      );
      this.invoice = response;
      this.buildInvoice();

      const element = document.getElementById('entire-thing');
      if (element) {
        this.html2pdfOptions.filename = `${this.getInvoiceId}.pdf`;
        html2pdf(element, this.html2pdfOptions);

        await new Promise((resolve) => {
          setTimeout(() => {
            document.getElementById('invoice-page').innerHTML = '';
            document.getElementById('credit-page').innerHTML = '';
            document.getElementById('payment-summary-page').innerHTML = '';
            resolve(null);
          }, 500);
        });
      }
    } catch (err) {
      console.error('Error fetching Credit Note ', err);
    }
  }

  private escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
  }

  getCurrencySymbol(
    type: 'summary' | 'totalDue' = 'summary',
    className?: string,
  ): string {
    const fontSize = type === 'summary' ? '10.5px' : '0.975rem';

    const fill = className?.includes('text-red-60') ? '#D61F1F' : '#231f20';

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1124.14 1256.39" height="${fontSize}" width="${fontSize}">
    <path fill="${fill}" d="M699.62,1113.02h0c-20.06,44.48-33.32,92.75-38.4,143.37l424.51-90.24c20.06-44.47,33.31-92.75,38.4-143.37l-424.51,90.24Z"/>
    <path fill="${fill}" d="M1085.73,895.8c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.33v-135.2l292.27-62.11c20.06-44.47,33.32-92.75,38.4-143.37l-330.68,70.27V66.13c-50.67,28.45-95.67,66.32-132.25,110.99v403.35l-132.25,28.11V0c-50.67,28.44-95.67,66.32-132.25,110.99v525.69l-295.91,62.88c-20.06,44.47-33.33,92.75-38.42,143.37l334.33-71.05v170.26l-358.3,76.14c-20.06,44.47-33.32,92.75-38.4,143.37l375.04-79.7c30.53-6.35,56.77-24.4,73.83-49.24l68.78-101.97v-.02c7.14-10.55,11.3-23.27,11.3-36.97v-149.98l132.25-28.11v270.4l424.53-90.28Z"/>
  </svg>`;
  }
}
